<template>
  <v-card class="mt-6">
    <v-card-title class="d-flex align-center">
      <div>
        <div class="subtitle-2 font-weight-bold">
          Impressoras
        </div>
        <div class="text-caption text--secondary">
          Cadastre impressoras para recibos ou comandas.
        </div>
      </div>
      <v-spacer />
      <v-btn
        color="primary"
        depressed
        small
        :loading="creating"
        :disabled="creating || loading"
        @click="addPrinter"
      >
        Adicionar impressora
      </v-btn>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <v-row dense>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="form.location"
            outlined
            dense
            label="Local"
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="form.model"
            outlined
            dense
            label="Modelo"
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="form.printer_key"
            outlined
            dense
            label="Key"
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
        >
          <v-text-field
            v-model="form.ip_address"
            outlined
            dense
            label="IP"
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          md="2"
        >
          <v-text-field
            v-model="form.port"
            outlined
            dense
            label="Porta"
            type="number"
            hide-details
          />
        </v-col>
      </v-row>

      <v-alert
        v-if="formError"
        type="error"
        dense
        outlined
        class="mt-2"
      >
        {{ formError }}
      </v-alert>

      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
        class="mt-4"
      />

      <v-simple-table class="mt-4">
        <thead>
          <tr>
            <th class="text-left">
              Local
            </th>
            <th class="text-left">
              Modelo
            </th>
            <th class="text-left">
              Key
            </th>
            <th class="text-left">
              IP
            </th>
            <th class="text-left">
              Porta
            </th>
            <th class="text-right">
              Ações
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && !printers.length">
            <td
              colspan="6"
              class="text-center text--secondary"
            >
              Nenhuma impressora cadastrada.
            </td>
          </tr>
          <tr
            v-for="printer in printers"
            :key="printer.id"
          >
            <td>
              <v-text-field
                v-model="printer.location"
                dense
                solo
                flat
                hide-details
                placeholder="Recepção"
              />
            </td>
            <td>
              <v-text-field
                v-model="printer.model"
                dense
                solo
                flat
                hide-details
                placeholder="Térmica"
              />
            </td>
            <td>
              <v-text-field
                v-model="printer.printer_key"
                dense
                solo
                flat
                hide-details
                placeholder="KITCHEN-01"
              />
            </td>
            <td>
              <v-text-field
                v-model="printer.ip_address"
                dense
                solo
                flat
                hide-details
                placeholder="192.168.0.100"
              />
            </td>
            <td>
              <v-text-field
                v-model.number="printer.port"
                dense
                solo
                flat
                hide-details
                type="number"
                placeholder="9100"
              />
            </td>
            <td class="text-right">
              <v-btn
                color="primary"
                text
                small
                :loading="isRowLoading(printer.id)"
                @click="updatePrinter(printer)"
              >
                Salvar
              </v-btn>
              <v-btn
                color="error"
                small
                :loading="isRowDeleting(printer.id)"
                @click="deletePrinter(printer)"
              >
                <v-icon
                  left
                  small
                >
                  mdi-delete-outline
                </v-icon>
                Excluir
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'Printers',
  data: () => ({
    printers: [],
    loading: false,
    creating: false,
    rowLoading: {},
    rowDeleting: {},
    formError: null,
    form: {
      location: '',
      model: '',
      printer_key: '',
      ip_address: '',
      port: 9100,
    },
  }),
  created() {
    this.fetchPrinters()
  },
  methods: {
    ...mapActions('printers', {
      listPrinters: 'list',
      createPrinterRequest: 'create',
      updatePrinterRequest: 'update',
      deletePrinterRequest: 'delete',
    }),
    async fetchPrinters() {
      this.loading = true
      try {
        const response = await this.listPrinters()
        this.printers = (response.data || []).map(item => ({
          ...item,
          port: item.port || 9100,
        }))
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Could not load printers'))
      } finally {
        this.loading = false
      }
    },
    validateForm() {
      if (!this.form.location || !this.form.printer_key || !this.form.ip_address) {
        return 'Local, Key e IP são obrigatórios.'
      }
      if (!this.form.port || Number.isNaN(Number(this.form.port))) {
        return 'Porta é obrigatória.'
      }
      if (this.form.port <= 0 || this.form.port > 65535) {
        return 'Porta deve estar entre 1 e 65535.'
      }

      return null
    },
    resetForm() {
      this.form = {
        location: '',
        model: '',
        printer_key: '',
        ip_address: '',
        port: 9100,
      }
      this.formError = null
    },
    async addPrinter() {
      const validation = this.validateForm()
      if (validation) {
        this.formError = validation

        return
      }

      this.creating = true
      this.formError = null
      try {
        const response = await this.createPrinterRequest({ ...this.form })
        this.printers.push(response.data)
        this.$toast.success('Impressora adicionada')
        this.resetForm()
      } catch (error) {
        this.formError = this.getErrorMessage(error, 'Não foi possível adicionar a impressora')
      } finally {
        this.creating = false
      }
    },
    async updatePrinter(printer) {
      this.$set(this.rowLoading, printer.id, true)
      try {
        const payload = {
          id: printer.id,
          location: printer.location,
          model: printer.model,
          printer_key: printer.printer_key,
          ip_address: printer.ip_address,
          port: printer.port,
        }
        await this.updatePrinterRequest(payload)
        this.$toast.success('Impressora atualizada')
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Não foi possível atualizar a impressora'))
      } finally {
        this.$set(this.rowLoading, printer.id, false)
      }
    },
    async deletePrinter(printer) {
      this.$set(this.rowDeleting, printer.id, true)
      try {
        await this.deletePrinterRequest(printer.id)
        this.printers = this.printers.filter(item => item.id !== printer.id)
        this.$toast.success('Impressora removida')
      } catch (error) {
        this.$toast.error(this.getErrorMessage(error, 'Não foi possível remover a impressora'))
      } finally {
        this.$set(this.rowDeleting, printer.id, false)
      }
    },
    isRowLoading(id) {
      return this.rowLoading[id] === true
    },
    isRowDeleting(id) {
      return this.rowDeleting[id] === true
    },
    getErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors)[0]
        if (Array.isArray(firstError)) {
          return firstError[0]
        }
        if (typeof firstError === 'string') {
          return firstError
        }
      }

      return error?.response?.data?.message || fallback || 'Erro inesperado'
    },
  },
}
</script>
